// ============================================================================
// NEW ACCOUNTS lifecycle helpers. The lifecycle itself is NOT decided here:
// the database decides it once — public.building_lifecycle(), surfaced as
// `lifecycle` on v_new_account_lifecycle — and the board loader attaches it to
// each card (2026-09-25, Ben: "the database owns the rule; the app reads it").
// A confirmed start sets the building Active, and a confirmed Active building
// always beats its planned date. This file only layers the board's own
// display state (`started_incomplete`) on top of that answer.
//
// Cowork 02 (2026-06-28): a card that has been CONFIRMED started but still has
// OPEN KEY requirements (no billing recipient, no price, no sub, etc.) must NOT
// disappear from the board. It becomes `started_incomplete` and stays on the
// board until every KEY requirement clears. `hasOpenKeyReqs` is sourced from the
// KEY-tier count in v_account_open_requirements.
// ============================================================================

import type { NewAccountSop } from '../../../shared/types';

export type Lifecycle =
  | 'draft'
  | 'cancelled'
  | 'recent_start'
  | 'started'
  | 'started_incomplete'
  | 'behind'
  | 'starting_soon'
  | 'new';

/** Local midnight today, for date-only comparisons against start_date. */
function today(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Parse a YYYY-MM-DD (or ISO) date string to a local Date at midnight. */
export function parseStartDate(s: string | null | undefined): Date | null {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Whole days until the planned start (negative = overdue). null if no date. */
export function daysUntilStart(row: Pick<NewAccountSop, 'start_date'>): number | null {
  const d = parseStartDate(row.start_date);
  if (!d) return null;
  return Math.round((d.getTime() - today().getTime()) / 86_400_000);
}

/** Countdown copy for the card / widget / pill (D2). */
export function countdownLabel(row: Pick<NewAccountSop, 'start_date'>): string | null {
  const n = daysUntilStart(row);
  if (n === null) return null;
  if (n < 0) return `${Math.abs(n)} day${Math.abs(n) === 1 ? '' : 's'} overdue`;
  if (n === 0) return 'Starts today';
  return `Starts in ${n} day${n === 1 ? '' : 's'}`;
}

type LifecycleRow = Pick<
  NewAccountSop,
  'is_draft' | 'status' | 'stage' | 'start_date' | 'started_confirmed_at'
> & {
  /** AFB-0064: manual archive stamp. Optional so callers holding older
   *  projections still type-check; absent is treated as "not archived". */
  archived_at?: string | null;
  /** building_lifecycle() answer from v_new_account_lifecycle. null when the
   *  building is closed (it has no lifecycle) or the row was not read. */
  lifecycle?: string | null;
};

/**
 * Lifecycle for a new-account card — the database's answer, read.
 *
 * @param row              The card, carrying `lifecycle` from the view.
 * @param hasOpenKeyReqs   Whether the card has >= 1 OPEN KEY requirement
 *                         (KEY-tier count > 0 in v_account_open_requirements).
 *                         A started card with open KEY work stays on the board
 *                         as `started_incomplete` until KEY clears. That is a
 *                         statement about missing information, not about dates.
 */
export function accountLifecycle(row: LifecycleRow, hasOpenKeyReqs = false): Lifecycle | null {
  const l = (row.lifecycle ?? null) as Lifecycle | null;
  if ((l === 'started' || l === 'recent_start') && hasOpenKeyReqs) return 'started_incomplete';
  return l;
}

/**
 * On the NEW ACCOUNTS board = in-flight: not draft, not cancelled, not cleanly
 * started. A `started_incomplete` card (confirmed start + open KEY info) STAYS
 * on the board so the missing info doesn't get lost.
 */
export function isOnBoard(row: LifecycleRow, hasOpenKeyReqs = false): boolean {
  if (row.is_draft) return false;
  // AFB-0064 (Ben, 2026-08-06). A card now leaves the board ONLY when a human
  // archives it — never on completion, never on age.
  //
  // Jose's complaint was that completed cards vanished to Account Management, so
  // there was nowhere left to audit what was still missing; he had been using the
  // Trello card as the single place to see gaps. The old rule dropped a card the
  // moment it was cleanly started or reached stage='Completed', which is exactly
  // when its remaining gaps (missing sub / rate / contract / date) matter most.
  //
  // Cancelled is the one non-manual exit, and it already is a deliberate act with
  // a reason attached, so it stays an exit.
  if (row.archived_at) return false;
  const l = accountLifecycle(row, hasOpenKeyReqs);
  return l !== 'cancelled' && l !== 'draft';
}

/**
 * Archived = the COMPLEMENT of the board for non-draft cards: cleanly started
 * (started / recent_start), cancelled, or on the terminal Completed stage.
 * These retain in the DB but drop off the board; the Archived lens (Task 3,
 * 2026-07-14) shows them read-only. Drafts are excluded — they have their own
 * resumable strip. This does NOT change board membership; it only names the
 * complement so nothing captured is unrecoverable.
 */
export function isArchived(row: LifecycleRow, hasOpenKeyReqs = false): boolean {
  if (row.is_draft) return false;
  // AFB-0064: archived now means EXPLICITLY archived (or cancelled), not
  // "old enough that we stopped showing it". Completion no longer archives —
  // a completed card sits in the Completed column with its gap indicators
  // still visible, which is the entire point of keeping it there.
  if (row.archived_at) return true;
  return accountLifecycle(row, hasOpenKeyReqs) === 'cancelled';
}

/**
 * May this card still change the LIVE building it points at?
 *
 * Ben, 2026-09-09: "Admin sometimes uses the new account SOP/checklist to finish
 * onboarding the account AFTER it is live. So the onboarding card MUST be a place
 * where you can change what is on the card UNTIL the card is closed / archived /
 * completed. At that point, it should not make changes to the live account."
 *
 * ⛔ THE GATE IS THE CARD'S STATE, NOT THE BUILDING'S. An earlier draft of this
 * proposed locking the card the moment the building went live, which is exactly
 * backwards: finishing onboarding after go-live is the ordinary case, not the
 * exception.
 *
 * ⛔ AND "CLOSED" MEANS ARCHIVED, NOT COMPLETED (Ben, 2026-09-11). His three
 * words above were one idea, not three gates. The process he was describing is:
 * onboarding finishes, then FINANCE archives the card. Completing the checklist
 * is the middle of that, not the end of it — locking there takes the pen away
 * while admin is still finishing the job it exists for. This function used to
 * stop a Completed card and it no longer does; the 28 Completed cards that had
 * been locked are open again.
 *
 * ⛔ AND IT IS NOT THE SAME QUESTION AS BOARD MEMBERSHIP. A completed card STAYS
 * on the board with its gaps visible (AFB-0064, Ben 2026-08-06) — Jose audits the
 * remaining gaps there. Archiving is what removes it, and Jose can now do that
 * himself; until 2026-09-11 archiving was superuser-only, so the one person who
 * performs this step could not.
 */
export function mayChangeTheLiveBuilding(row: LifecycleRow): boolean {
  if (row.archived_at) return false;
  if (row.is_draft) return false;
  if (row.stage === 'Cancelled') return false;
  if (row.status === 'Cancelled') return false;
  return true;
}

/** Why the card cannot change the live building, in words a person reads. */
export function whyLocked(row: LifecycleRow): string | null {
  if (mayChangeTheLiveBuilding(row)) return null;
  if (row.archived_at)
    return 'This card has been archived, so it no longer changes the live building.';
  if (row.is_draft) return 'This is still a draft, so it has no live building to change.';
  return 'This card was cancelled, so it no longer changes the live building.';
}

/** Behind = on the board, planned start passed, and (reconcilable) has a
 *  facility so "Confirm started" / "Edit start date" can act on it. */
export function isBehind(
  row: LifecycleRow & Pick<NewAccountSop, 'facility_id'>,
  hasOpenKeyReqs = false,
): boolean {
  return accountLifecycle(row, hasOpenKeyReqs) === 'behind' && !!row.facility_id;
}
